<template>
  <layout>
    <template v-slot:header>
      <h2>Manufacturer {{ manufacturer.name }}</h2>
      <BreadCrumbs activeItem="View Manufacturer" :crumbs="{ home: 'Dashboard', manufacturers: 'Manufacturers'}"/>
      <flash-messages />
      <AppHead :title="`Manufacturer ${manufacturer.name}`" />
    </template>
        
    <template v-slot:default>


      <img class="mb-3" :src="manufacturer.logo_url" v-if="manufacturer.logo"/>

      <div>
        <h3>Top Sellers</h3>
            <DataTable
              :value="sales" :paginator="false" sortField="qty" :sortOrder="-1" class="p-datatable-sm admin-datatable thead-ui" 
              dataKey="product.sku"
              @row-click="(e) => this.$inertia.visit(route('admin.products.view', [e.data.product.id]))">
              <Column field="date" header="Product" headerStyle="width: 3em">
                <template #body="slotProps">
                  {{ slotProps.data.product.sku }}
                </template>
              </Column>
              <Column field="qty" header="Total" headerStyle="width: 3em"></Column>
              <Column field="qty_30" header="30" headerStyle="width: 3em"></Column>
              <Column field="qty_60" header="60" headerStyle="width: 3em"></Column>
              <Column field="qty_90" header="90" headerStyle="width: 3em"></Column>
              <Column field="qty_120" header="120" headerStyle="width: 3em"></Column>
              <Column field="product.current_inventory" header="Inventory" headerStyle="width: 3em"></Column>
            </DataTable>
      </div>


      <form @submit.prevent="update">
        <div class="row mb-3">
          <div class="col-12 col-lg-6 mb-3 mb-lg-0">
            <card class="h-100" title="Manufacturer Info">
              <div class="row">
                <div class="form-group col-12 col-xl-6">
                  <jet-label for="name" value="Name" />
                  <jet-input id="name"  type="text" class="form-control" v-model="form.name" />
                  <jet-input-error :message="form.errors.name" class="mt-2" />
                </div>

                <div class="form-group col-12 col-xl-6">
                  <jet-label for="short_name" value="Code" />
                  <jet-input id="short_name"  type="text" class="form-control" v-model="form.short_name" />
                  <jet-input-error :message="form.errors.short_name" class="mt-2" />
                </div>
              </div>

              <div class="row">
                <div class="form-group col-12 col-xl-6">
                  <jet-label for="po_account_number" value="Account # (used for PO)" />
                  <jet-input id="po_account_number"  type="text" class="form-control" v-model="form.po_account_number" />
                  <jet-input-error :message="form.errors.po_account_number" class="mt-2" />
                </div>
              </div>
            </card>
          </div>

          <div class="col-12 col-lg-6">
            <card class="h-100" icon="fas fa-upload" headerStyle="primary" title="Logo">
              <img class="mb-3" :src="manufacturer.logo_url" v-if="manufacturer.logo"/>

              <!-- tax exempt form -->
              <div class="form-group">
                <!-- Profile Photo File Input -->
                <input type="file" hidden
                      ref="logo"
                      @change="uploadLogo">

                <jet-button class="mt-2 mr-2 btn-short" @click.prevent="selectLogo">
                  Upload Logo
                </jet-button>
                <jet-input-error :message="form.errors.document" class="mt-2" />
              </div>
            </card> 
          </div>
        </div>

        <card title="Address">
          <div class="row">
            <div class="col-lg-4 form-group">
              <jet-label for="company_name" value="Company Name (official mailing)" />
              <jet-input id="company_name"  type="text" class="form-control" v-model="form.company_name" />
              <jet-input-error :message="form.errors.company_name" class="mt-2" />
            </div>

            <div class="col-lg-4 form-group">
              <jet-label for="address1" value="Address"></jet-label>
              <GMapAutocomplete
                id="editaddress1"
                class="form-control"
                placeholder="Start typing address.."
                @place_changed="getAddressData"
              />
              <div v-if="form.errors.address1">{{ form.errors.address1 }}</div>
            </div>

            <div class="col-lg-4 form-group">
              <jet-label for="address2" value="Line 2"></jet-label>
              <input id="address2" class="form-control" type="text" v-model="form.address2"/>
              <div v-if="form.errors.address2">{{ form.errors.address2 }}</div>
            </div>
          </div>

          <div class="row">
            <div class="col-lg-4 form-group">
              <jet-label for="city" value="City"></jet-label>
              <input id="city" class="form-control" type="text" v-model="form.city"/>
              <div v-if="form.errors.city">{{ form.errors.city }}</div>
            </div>

            <div class="col-lg-4 form-group">
              <jet-label for="state" value="State"></jet-label>
              <select v-model="form.state" class="form-control">
              <option v-for="state in this.states" :key="state.name" :value="state.code">
                {{ state.code }} - {{ state.name }}
              </option>
              </select>
              <div v-if="form.errors.state">{{ form.errors.state }}</div>
            </div>

            <div class="col-lg-4 form-group">
              <jet-label for="postcode" value="Zip"></jet-label>
              <input id="postcode" class="form-control" type="text" v-model="form.postcode"/>
              <div v-if="form.errors.postcode">{{ form.errors.postcode }}</div>
            </div>
          </div>
        </card>

        <div class="bottom-button mt-4">
          <jet-button class="px-5" btnStyle="success" :disable="form.processing" type="submit">Edit Manufacturer</jet-button>
        </div>
      </form>

      <card icon="fas fa-address-book" headerStyle="dark" title="Contacts" class="mt-5">
        <div class="py-3">
          <div class="table-responsive">
            <table v-if="contacts.length" class="table table-striped table-sm">
              <thead>
                <tr>
                  <th>Send PO</th>
                  <th>Show on PO</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Title</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in contacts" :key="item.id">
                  <td>
                    <i v-if="item.send_po" class="fas fa-check fa-lg text-success"></i>
                  </td>
                  <td>
                    <i v-if="item.show_po" class="fas fa-check fa-lg text-success"></i>
                  </td>
                  <td>{{ item.first_name }} {{ item.last_name }}</td>
                  <td><Email v-model="item.email" /></td>
                  <td>{{ item.title }}</td>
                  <td class="d-flex justify-content-end pr-5">
                    <jet-button class="btn-short text-nowrap" icon="fas fa-edit" @click.prevent="showEdit(item)">Edit</jet-button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <jet-button icon="fas fa-plus" @click="showEdit">Add Contact</jet-button>
        </div>
      </card>

      <template>
        <jet-dialog-modal id="contactModal">
          <template #title>Contact</template>
          <template #content>
            <div class="form-group">
              <jet-label for="first_name" value="First Name"></jet-label>
              <input id="first_name" class="form-control" type="text" v-model="formContact.first_name"/>
            </div>
            <div class="form-group">
              <jet-label for="last_name" value="Last Name"></jet-label>
              <input id="last_name" class="form-control" type="text" v-model="formContact.last_name"/>
            </div>
            <div class="form-group">
              <jet-label for="email" value="Email"></jet-label>
              <input id="email" class="form-control" type="text" v-model="formContact.email"/>
            </div>
            <div class="form-group">
              <jet-label for="title" value="Title"></jet-label>
              <input id="title" class="form-control" type="text" v-model="formContact.title"/>
            </div>
            <div class="form-group">
              <jet-label for="phone" value="Phone"></jet-label>
              <input id="phone" class="form-control" type="text" v-model="formContact.phone"/>
            </div>
            <div class="form-check">
              <input type="checkbox" id="send_po" class="form-check-input" v-model="formContact.send_po" /> 
              <jet-label for="send_po" value="Send PO"></jet-label>
            </div>
            <div class="form-check">
              <input type="checkbox" id="show_po" class="form-check-input" v-model="formContact.show_po" /> 
              <jet-label for="show_po" value="Show PO"></jet-label>
            </div>
          </template>

          <template #footer>
            <jet-button @click="saveContact" data-dismiss="modal">Save</jet-button>
            <jet-danger-button class="ml-2" data-dismiss="modal">Cancel</jet-danger-button>
          </template>
        </jet-dialog-modal>
      </template>
    </template>
  </layout>
</template>

<script>
  import Layout from '@/Shared/Admin/Layout'
  import FileInput from '@/Shared/FileInput'
  import TextInput from '@/Shared/TextInput'
  import SelectInput from '@/Shared/SelectInput'

  import FlashMessages from '@/Shared/FlashMessages'
  import JetButton from '@/Jetstream/Button'
  import JetFormSection from '@/Jetstream/FormSection'
  import JetInput from '@/Jetstream/Input'
  import JetCheckbox from '@/Jetstream/Checkbox'
  import JetInputError from '@/Jetstream/InputError'
  import JetLabel from '@/Jetstream/Label'
  import JetActionMessage from '@/Jetstream/ActionMessage'
  import JetDialogModal from '@/Jetstream/DialogModal'
  import JetDangerButton from '@/Jetstream/DangerButton'
  import BreadCrumbs from '@/Shared/BreadCrumbs'
  import Helpers from '@/Helpers'

  export default {
    components: {
      FileInput,
      SelectInput,
      TextInput,
      JetActionMessage,
      JetButton,
      JetFormSection,
      JetInput,
      JetCheckbox,
      JetInputError,
      JetLabel,
      JetDialogModal,
      JetDangerButton,
      Layout,
      FlashMessages,
      BreadCrumbs
    },
    remember: 'form',
    props: {
      manufacturer: Object,
      address: Object,
      states: Array,
      contacts: Array,
      sales: Array
    },
    data() {
      return {
        form: this.$inertia.form({
          _method: 'put',
          name: this.manufacturer.name,
          short_name: this.manufacturer.short_name,
          po_account_number: this.manufacturer.po_account_number,
          company_name: this.address.company_name,
          address1: this.address.address1,
          address2: this.address.address2,
          city: this.address.city,
          state: this.address.state,
          postcode: this.address.postcode
        }),
        formLogo: this.$inertia.form({
          _method: 'put',
          logo: null
        }),
        formContact: this.$inertia.form({
          _method: 'put',
          address_id: null,
          first_name: null,
          last_name: null,
          email: null,
          phone: null,
          title: null,
          send_po: null,
          show_po: null
        }),
      }
    },
    mounted() {
      document.getElementById('editaddress1').value = this.address.address1
    },
    methods: {
      update() {
        this.form.address1 = document.getElementById('editaddress1').value
        this.form.post(this.route('manufacturers.update', this.manufacturer.id))
      },
      getAddressData(info) {
        const city = Helpers.getAddressComponent('locality', info)
        const state = Helpers.getAddressComponent('administrative_area_level_1', info)
        const zipcode = Helpers.getAddressComponent('postal_code', info)

        const street_number = Helpers.getAddressComponent('street_number', info)
        const route = Helpers.getAddressComponent('route', info)
        let address1 = '';
        if (street_number.length && route.length) {
          address1 = street_number[0].long_name + ' ' + route[0].short_name;
        } else if (info.name) {
          address1 = info.address
        }
        this.form.address1 = address1
        document.getElementById('editaddress1').value = address1
     
        if (city.length) this.form.city = city[0].long_name
        if (state.length) this.form.state = state[0].short_name
        if (zipcode.length) this.form.postcode = zipcode[0].long_name
      },
      selectLogo() {
        this.$refs.logo.click();
      },
      uploadLogo() {
        if (this.$refs.logo) {
          this.formLogo.logo = this.$refs.logo.files[0]

          this.formLogo.post(this.route('manufacturers.uploadLogo', this.manufacturer.id), {
            onSuccess: () => this.formLogo.reset('logo'),
          })
        }
      },
      showEdit(item) {
        if (item) {
          this.formContact.address_id = item.id;
          this.formContact.first_name = item.first_name;
          this.formContact.last_name = item.last_name;
          this.formContact.email = item.email;
          this.formContact.phone = item.phone;
          this.formContact.title = item.title;
          this.formContact.send_po = item.send_po == 1;
          this.formContact.show_po = item.show_po == 1;
        } else this.formContact.reset()
        $('#contactModal').modal('show')
      },
      saveContact() {
        $('#contactModal').modal('hide')
        if (this.formContact.address_id) {
          this.formContact.post(this.route('manufacturers.editContact', this.formContact.address_id));
        } else {
          this.formContact.post(this.route('manufacturers.addContact', this.manufacturer.id));
        }
      }
    },
  }
</script>
